// frontend_react/src/components/paymodal/Receipt.jsx

import React, { useMemo } from 'react';
import styled from 'styled-components';

import logo from '../../assets/Logo_CrownOnly.svg';
import { calculate_unit_ttc_from_ht } from '../../utils/tax_utils';

/* ─── TOKENS ─── */
const C = {
  text:    '#D9D9D9',
  muted:   'rgba(255,255,255,0.60)',
  accent:  '#FF3561',
  bg:      '#15203F',
  line:    'rgba(255,255,255,0.20)',
};

/* ─── HELPERS ─── */
const fmtEUR = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;

function normalizeVatRatePercent(r) {
  const v = Number(r || 0);
  return Number.isFinite(v) ? (v <= 1 ? v * 100 : v) : 0;
}

function isDiscountLikeLine(line) {
  const n = String(line?.name || '').toLowerCase();
  return ['remise','discount','avoir','refund','rembourse'].some((k) => n.includes(k));
}

/* ─── COMPONENT ─── */
export default function Receipt({
  lines = [],
  full,
  payment,
  showVatDetail = true,
  showLegalMentions = true,
}) {
  const data = full?.lines ?? lines;

  const { productLines, discountLines } = useMemo(() => {
    const prod = [], disc = [];
    (data || []).forEach((l) => (isDiscountLikeLine(l) ? disc : prod).push(l));
    return { productLines: prod, discountLines: disc };
  }, [data]);

  const computedLines = useMemo(() =>
    (productLines || []).map((line) => {
      const qty     = Number(line.quantity || 0);
      const unitHT  = Number(line.price_ht || 0);
      const vatPct  = normalizeVatRatePercent(line.vat_rate);
      const lineHT  = unitHT * qty;
      const lineTTC = calculate_unit_ttc_from_ht(lineHT, vatPct);
      const lineTVA = lineTTC - lineHT;
      return { ...line, qty, unitTTC: qty > 0 ? lineTTC / qty : 0, vatPct, lineHT, lineTVA, lineTTC };
    }),
  [productLines]);

  const totals = useMemo(() => {
    if (full && Number.isFinite(Number(full.total_ttc)))
      return { total_ht: Number(full.total_ht || 0), total_vat: Number(full.total_vat || 0), total_ttc: Number(full.total_ttc || 0) };
    const totalHT  = computedLines.reduce((s, l) => s + l.lineHT,  0);
    const totalTVA = computedLines.reduce((s, l) => s + l.lineTVA, 0);
    return { total_ht: totalHT, total_vat: totalTVA, total_ttc: totalHT + totalTVA };
  }, [full, computedLines]);

  const vatMap = useMemo(() => {
    if (!showVatDetail) return {};
    return computedLines.reduce((acc, l) => {
      const k = l.vatPct.toFixed(2);
      if (!acc[k]) acc[k] = { total_ht: 0, total_vat: 0 };
      acc[k].total_ht  += l.lineHT;
      acc[k].total_vat += l.lineTVA;
      return acc;
    }, {});
  }, [showVatDetail, computedLines]);

  const adjustments = useMemo(() => {
    const fromFull = Number(full?.discount_total || full?.total_discount || full?.refund_total || full?.total_refund || 0);
    const fromLines = (discountLines || []).reduce((s, l) =>
      s + calculate_unit_ttc_from_ht(Number(l.price_ht || 0) * Number(l.quantity || 1), normalizeVatRatePercent(l.vat_rate)), 0);
    return Number(Math.abs(fromFull) > 0.0001 ? fromFull : fromLines || 0);
  }, [full, discountLines]);

  const rendu = payment && payment.amount > totals.total_ttc ? payment.amount - totals.total_ttc : 0;

  return (
    <Ticket>

      {/* ── EN-TÊTE ── */}
      <TitleBlock>
        <BrandRow>
          <LogoWrap><img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></LogoWrap>
          <PNatural style={{ margin: 0 }}>
            <T1>Queen's </T1><TAccent>Garden</TAccent>
          </PNatural>
        </BrandRow>
        <PCenter><TMuted>14 rue André Bonnenfant</TMuted></PCenter>
        <PCenter><TMuted>78100 Saint-Germain-en-Laye</TMuted></PCenter>
        <PCenter><TMuted>01 75 79 68 07</TMuted></PCenter>
      </TitleBlock>

      <Spacer />

      {/* ── DATE / NUMÉRO ── */}
      {showLegalMentions && full && (
        <Row style={{ padding: '0 10px' }}>
          <PFlex><T2>{full?.date ? new Date(full.date).toLocaleString('fr-FR') : '-'}</T2></PFlex>
          <PNatural style={{ margin: 0, textAlign: 'right' }}><TMuted>N°{full?.number ?? '-'}</TMuted></PNatural>
        </Row>
      )}

      <Spacer />

      {/* ── ARTICLES ── */}
      <Section>
        <PCenter><TBold>Articles</TBold></PCenter>

        {computedLines.map((l, idx) => (
          <ArticleBlock key={idx}>
            {/* Nom centré */}
            <PCenter><T2>{l.name}</T2></PCenter>

            {/* Qté × prix | Total */}
            <Row>
              <PNatural style={{ margin: 0 }}>
                <T2>{l.qty}</T2><TMuted> × {fmtEUR(l.unitTTC)}</TMuted>
              </PNatural>
              <PRight><T2>{fmtEUR(l.lineTTC)}</T2></PRight>
            </Row>

            {/* Remise par ligne (optionnelle) */}
            {(l.discount_percent || l.discount_amount || l.remise_percent || l.remise_amount) && (
              <Row style={{ paddingLeft: 10 }}>
                <PNatural style={{ margin: 0 }}><TMuted>→</TMuted></PNatural>
                <PFlex><TMuted>Remise (-{l.discount_percent ?? l.remise_percent ?? ''}%)</TMuted></PFlex>
                <PRight><TMuted>- {fmtEUR(l.discount_amount ?? l.remise_amount ?? 0)}</TMuted></PRight>
              </Row>
            )}
          </ArticleBlock>
        ))}

        {/* Lignes remise/avoir encodées en ligne séparée */}
        {discountLines.map((l, idx) => (
          <Row key={`disc-${idx}`} style={{ paddingLeft: 10 }}>
            <PNatural style={{ margin: 0 }}><TMuted>→</TMuted></PNatural>
            <PFlex><TMuted>{l.name}</TMuted></PFlex>
            <PRight>
              <TMuted>{fmtEUR(calculate_unit_ttc_from_ht(
                Number(l.price_ht || 0) * Number(l.quantity || 1),
                normalizeVatRatePercent(l.vat_rate)
              ))}</TMuted>
            </PRight>
          </Row>
        ))}
      </Section>

      <Spacer />

      {/* ── TOTAL ── */}
      <Section>
        <PCenter><TBold>Total</TBold></PCenter>

        {adjustments > 0 && (
          <Row>
            <PFlex><T2>Remises</T2></PFlex>
            <PRight><T2>-{fmtEUR(adjustments)}</T2></PRight>
          </Row>
        )}

        <Row>
          <PFlex><T2>Total TTC</T2></PFlex>
          <PRight><T2>{fmtEUR(totals.total_ttc)}</T2></PRight>
        </Row>

        {showVatDetail && Object.entries(vatMap).map(([rate, { total_ht, total_vat }]) => (
          <Row key={rate}>
            <PFlex>
              <T2>HT: {fmtEUR(total_ht)} </T2>
              <TMuted>({Number(rate).toFixed(2)}%)</TMuted>
            </PFlex>
            <PRight><T2>TVA: {fmtEUR(total_vat)}</T2></PRight>
          </Row>
        ))}
      </Section>

      <Spacer />

      {/* ── PAIEMENTS ── */}
      {payment && (
        <Section>
          <PCenter><TBold>Paiements</TBold></PCenter>

          <Row>
            <PFlex><T2>{String(payment.means_payment || '').toUpperCase()}</T2></PFlex>
            <PRight><T2>{fmtEUR(payment.amount)}</T2></PRight>
          </Row>

          {rendu > 0 && (
            <Row>
              <PFlex><T2>Rendu</T2></PFlex>
              <PRight><T2>{fmtEUR(rendu)}</T2></PRight>
            </Row>
          )}

          <Row>
            <PFlex><T2>Total</T2></PFlex>
            <PRight><T2>{fmtEUR(totals.total_ttc)}</T2></PRight>
          </Row>
        </Section>
      )}

      <Spacer />

      {/* ── INFOS TICKET ── */}
      {showLegalMentions && full && (full?.key || full?.id) && (
        <InfoBlock>
          {full?.key && <PCenter><TSmall>Clé : {full.key}</TSmall></PCenter>}
          {full?.id  && <PCenter><TSmall>{full.id}</TSmall></PCenter>}
        </InfoBlock>
      )}

      <Spacer />

      {/* ── FOOTER ── */}
      {showLegalMentions && (
        <InfoBlock>
          <PCenter><TMuted>Merci pour votre visite, à bientôt !</TMuted></PCenter>
          <PCenter><TMuted>rcs versailles 793991928</TMuted></PCenter>
        </InfoBlock>
      )}

    </Ticket>
  );
}

/* ═══════════════════════════
   DESIGN SYSTEM — tokens → composants
═══════════════════════════ */

/* Conteneur principal */
const Ticket = styled.div`
  width: 459px;
  padding: 20px;
  background: ${C.bg};
  border-radius: 20px;
  outline: 1px rgba(255,255,255,0.10) solid;
  outline-offset: -1px;
  display: inline-flex;
  flex-direction: column;
  gap: 0;
  font-family: Inter, system-ui, sans-serif;
  box-sizing: border-box;

  @media print {
    width: 80mm;
    border-radius: 0;
    outline: none;
    padding: 8px;
  }
`;

/* Layout */
const TitleBlock = styled.div`
  align-self: stretch;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const BrandRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const LogoWrap = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
`;

/* Séparateur */
const Spacer = styled.div`
  align-self: stretch;
  height: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &::after {
    content: '';
    display: block;
    width: 100%;
    border-top: 1px solid ${C.line};
  }
`;

/* Section générique (Articles, Total, Paiements…) */
const Section = styled.div`
  align-self: stretch;
  padding: 0 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/* Ligne gauche | droite */
const Row = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

/* Bloc article */
const ArticleBlock = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Infos / footer */
const InfoBlock = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

/* ─── Cellules <p> ─── */
const PFlex    = styled.p`flex: 1 1 0; margin: 0;`;
const PRight   = styled.p`text-align: right; margin: 0;`;
const PCenter  = styled.p`align-self: stretch; text-align: center; margin: 0;`;
const PNatural = styled.p`margin: 0;`;

/* ─── Typographie ─── */
const baseText = `
  font-size: 20px;
  font-family: Inter, system-ui, sans-serif;
  font-weight: 500;
  line-height: 1.4;
  word-wrap: break-word;
`;

const T2      = styled.span`${baseText} color: ${C.text};`;
const TMuted  = styled.span`${baseText} color: ${C.muted};`;
const TBold   = styled.span`${baseText} font-weight: 700; color: ${C.text};`;
const TAccent = styled.span`${baseText} font-weight: 700; font-size: 32px; color: ${C.accent};`;
const T1      = styled.span`${baseText} font-weight: 700; font-size: 32px; color: ${C.text};`;
const TSmall  = styled.span`${baseText} font-size: 16px; color: ${C.muted};`;